/**
 * The request backstop (Cartographer doc 15 §7, W5-6): `ObservatoryRequestsBackstopController`,
 * `api/observatory/requests` — the open Cartographer requests that reached rung 3, and the interventions a
 * Lighthouse administrator may make on them. Mirrors `Contracts/Models/ObservatoryRequestModels.cs`.
 *
 * Every kind, state, route, tier and reason is its enum member's NAME (the API registers no global enum converter).
 * Every instant is a UTC ISO string with an offset. No requester or resolver id is served — only the display names
 * Cartographer stamped — except the assignee list, whose ids are what a reassignment names.
 */

/** Why a request is on the backstop. */
export type BackstopReason = 'Overdue' | 'NobodyCouldBeTold' | 'AwaitingLighthouseAdmin'

/** `GET api/observatory/requests/summary` — what the rail badge reads. */
export interface RequestBackstopSummary {
  /** False when the request table does not exist (migration AddCartographerRequest); every count is then null. */
  storeProvisioned: boolean
  /** Open requests on the backstop. */
  openCount: number | null
  overdueCount: number | null
  nobodyCouldBeToldCount: number | null
  awaitingLighthouseAdminCount: number | null
  /** When the longest-waiting of them arrived. Null when none is there. */
  oldestOnBackstopSince: string | null
}

/** One request as the backstop reads it — Cartographer's A9 projection for a Lighthouse administrator. */
export interface BackstopRequest {
  id: string
  /** `Commission`, `ContentAccess`, `PlatformAccess` or `Endorsement`. */
  kind: string
  /** The CartographerRequestState name. */
  state: string
  /** `None`, `Report` or `Workspace`. */
  targetKind: string
  targetId: string | null
  targetName: string | null
  targetDisclosed: boolean
  /** Platform access: who is to be given access, as the requester wrote it. */
  subjectText: string | null
  /** Commission: what is to be built. */
  title: string | null
  requestNote: string | null
  requestedRole: string | null
  accessDurationDays: number | null
  licenceLevel: string | null
  requesterDisplayName: string | null
  /** The RequestRouting name. */
  routedTo: string
  routedToDisplayName: string | null
  assigneeDisplayName: string | null
  resolvedByDisplayName: string | null
  evidence: string | null
  resolutionNote: string | null
  createdAt: string
  dueAt: string | null
  /** The EscalationTier name; `Admin` is the backstop. */
  escalationTier: string
  firstNotifiedAt: string | null
  escalatedAt: string | null
  resolvedAt: string | null
  retractionRequestedAt: string | null
  /** Open and at the Admin tier: what the list holds. */
  onBackstop: boolean
  /** When it reached the backstop; null when it is not on it. */
  onBackstopSince: string | null
  /** Why it is on the backstop; null when it is not on it. */
  backstopReason: BackstopReason | null
}

/** `GET api/observatory/requests` — one page, longest on the backstop first. */
export interface BackstopRequestList {
  items: BackstopRequest[]
  skip: number
  top: number
  totalCount: number
}

/** One audit row recorded against a request. */
export interface BackstopAuditEntry {
  id: string
  timestamp: string
  /** One of the sixteen `Request*` operations. */
  operation: string
  userName: string | null
  /** The row's detail JSON, as recorded. */
  details: string | null
}

/** `GET api/observatory/requests/{id}` — the request, what the matrix lets a Lighthouse administrator do, and its trail. */
export interface BackstopRequestDetail {
  request: BackstopRequest
  /**
   * RequestVerb names: some of `ForceClose`, `Forward`, `Grant` (sent to `/attest`) and `Refuse`. Empty when the request
   * is not on the backstop: the server refuses every verb on it then, 409 `NotOnBackstop`.
   */
  verbs: string[]
  /** RequestOperation names: some of `Reassign`, `SetDueAt`, `Reroute`. Empty, likewise, off the backstop. */
  operations: string[]
  /** The RequestRouting names a reroute accepts. */
  rerouteTo: string[]
  history: BackstopAuditEntry[]
}

/** One live member of a commission's workspace. */
export interface BackstopAssignee {
  /** What a reassignment names. */
  userId: string
  displayName: string | null
  role: string
  isCurrentAssignee: boolean
}

export interface BackstopAssigneeList {
  items: BackstopAssignee[]
}

/** The list endpoint's own default and maximum page. */
export const BACKSTOP_PAGE_SIZE = 50
